import { FormErrorMessage } from "@components/WAChatbutton/WAform/WidgetForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { checkStatus, parseJSON } from "@utils/fetch";
import { errMsgFocusStyle } from "@utils/form";
import qrcode from "@utils/qrcode";
import { supabase } from "@utils/supabase";
import axios from "axios";
import Image from "next/image";
import React, { ComponentType } from "react";
import { Controller, useForm } from "react-hook-form";
import { IconType } from "react-icons";
import { FiInfo } from "react-icons/fi";
import { IoCloseOutline, IoWarningOutline } from "react-icons/io5";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import ReactModal from "react-modal";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { ClipLoader } from "react-spinners";
import { v4 as uuid } from "uuid";
import { z } from "zod";

const Modal = ReactModal as unknown as ComponentType<ReactModal.Props>;
interface AIChatbotFormProps {
    wanumber: string;
    websiteUrl: string;
}

const AIChatbotFormSchema = z.object({
    wanumber: z.string({ required_error: "Whatsapp number is required", invalid_type_error: "Phone number must be a string" }).refine(
        (value) => {
            return isValidPhoneNumber(value);
        },
        { message: "Enter a valid phone number for this field." }
    ),
    websiteUrl: z
        .string({ required_error: "Website URL is required" })
        .min(3, "Enter a value for this field.")
        .regex(
            /^(https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*)?(\?[a-zA-Z0-9\-_=]+=[a-zA-Z0-9\-_%]+(&[a-zA-Z0-9\-_=]+=[a-zA-Z0-9\-_%]+)*)?$/,
            "Enter a valid url"
        ),
});

const inputStyle: React.CSSProperties = { padding: "10px", margin: "0", backgroundColor: "white" };

const Styles: ReactModal.Styles = {
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 2,
    },
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        transform: "translate(-50%, -50%)",
        backgroundColor: "white",
        padding: "16px",
        paddingBottom: "40px",
        width: "90%",
        height: "auto",
        maxHeight: "65%",
        maxWidth: "500px",
        margin: "auto",
    },
};

type RealtimeMessage = {
    message: string;
    status: string;
    url?: string;
};

const useBroadcast = (channelName: string) => {
    const [message, setMessage] = React.useState<RealtimeMessage | null>(null);

    React.useEffect(() => {
        const channel = supabase.channel(channelName);

        channel.on<RealtimeMessage>("broadcast", { event: "websiteToAIBotFlow" }, (data) => {
            console.log("New message received:", data);
            setMessage(data.payload);
        });

        channel.subscribe();

        return () => {
            channel.unsubscribe();
        };
    }, [channelName]);

    const sendMessage = (message: RealtimeMessage) => {
        supabase.channel(channelName).send({
            type: "broadcast",
            event: "message",
            payload: message,
        });
    };

    const clearMessage = () => {
        setMessage(null);
    };

    return { message, sendMessage, clearMessage };
};

interface CreateYourAIChatbotFormType {
    version?: "v2";
    submitButtonLabel?: string;
}
const CreateYourAIChatbotForm: React.FC<CreateYourAIChatbotFormType> = ({ version, submitButtonLabel }) => {
    const [country, setCountry] = React.useState();
    const [quality, setQuality] = React.useState<"single_page" | "medium" | "high">("medium");
    const [isOptionsOpen, setIsOptionsOpen] = React.useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const [qr, setQr] = React.useState<string>("");
    const [socketId, setSocketId] = React.useState<string>("");
    const { message, clearMessage } = useBroadcast(socketId);

    const phoneInputRef = React.useRef<HTMLInputElement | null>(null);

    const generateQr = async (url: string) => {
        const qrCode = await qrcode(url);
        setQr(qrCode);
    };

    React.useEffect(() => {
        if (message?.status === "success") {
            generateQr(message?.url as string);
        }
    }, [message]);

    const Icon: IconType = isOptionsOpen ? LuChevronUp : LuChevronDown;

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<AIChatbotFormProps>({ resolver: zodResolver(AIChatbotFormSchema) });

    const onSubmit = async (data: AIChatbotFormProps) => {
        const { wanumber, websiteUrl } = data;
        const sanitizedNumber = wanumber.split(" ").join("");
        const phoneNumber = sanitizedNumber.startsWith("+") ? sanitizedNumber.substring(1) : sanitizedNumber;

        clearMessage();
        setQr("");
        const id = uuid();
        setSocketId(id);
        setIsModalOpen(true);
        await axios.post("/api/v1/website-to-ai-bot", {
            website: websiteUrl,
            socketId: id,
            phoneNumber,
            quality,
        });
    };

    const renderModalContent = React.useMemo(() => {
        if (!qr && message?.status !== "error" && message?.status !== "success") {
            return <ClipLoader color={"#3364FA"} loading={true} size={70} />;
        }
        if (message?.status === "error") {
            return <IoWarningOutline color="#f55353" size={60} />;
        }
        if (qr) {
            return (
                <div className="w-full">
                    <div className="w-full flex items-center justify-center gap-2">
                        <Image width={36} height={36} src="https://assets.gallabox.com/gb-home/new-zapier/wa-w-whiteborder.svg" alt="whatsapp icon" />
                        <a className="text-blue-500 lg:text-base text-sm" target="_blank" rel="noreferrer" href={message?.url}>
                            {message?.url}
                        </a>
                    </div>
                    <img src={qr} alt="QR Code" />
                </div>
            );
        }
    }, [qr, message]);

    React.useEffect(() => {
        fetch("https://www.iplocate.io/api/lookup/json")
            .then(checkStatus)
            .then(parseJSON)
            .then((res) => {
                setCountry(res.country_code);
            })
            .catch(console.log);
    }, []);

    const whatsAppNumberField = (
        <div className="flex flex-col gap-2 mb-6 w-full">
            <AIChatbotFormLabel htmlFor="wanumber">WhatsApp number</AIChatbotFormLabel>
            <Controller
                name="wanumber"
                control={control}
                render={({ field }) => {
                    return (
                        <>
                            <PhoneInput
                                style={{ marginBottom: "0px", ...(errors.wanumber?.message ? errMsgFocusStyle : {}) }}
                                className="text-base font-normal"
                                placeholder="Enter your WhatsApp number"
                                defaultCountry={country ?? "IN"}
                                countryCallingCodeEditable={false}
                                international={true}
                                inputRef={(ref: HTMLInputElement) => {
                                    field.ref(ref);
                                    phoneInputRef.current = ref;
                                }}
                                {...field}
                                value={field.value}
                            />
                            {errors.wanumber?.message && <FormErrorMessage>{errors.wanumber.message}</FormErrorMessage>}
                        </>
                    );
                }}
            />
        </div>
    );

    const websiteURLField = (
        <div className="flex flex-col gap-2 pb-8 w-full">
            <AIChatbotFormLabel htmlFor="websiteUrl">Website URL</AIChatbotFormLabel>
            <input
                style={{ ...inputStyle, ...(errors.websiteUrl?.message ? errMsgFocusStyle : {}), height: "50px" }}
                id="websiteUrl"
                type="url"
                placeholder="Enter your website URL"
                className="text-base font-normal"
                {...register("websiteUrl")}
            />
            {errors.websiteUrl && <FormErrorMessage>{errors.websiteUrl.message}</FormErrorMessage>}
        </div>
    );

    const advancedOptions = (
        <div className="flex flex-col gap-2 pb-8">
            <AIChatbotFormLabel htmlFor="quality" onClick={() => setIsOptionsOpen(!isOptionsOpen)}>
                <span className="flex gap-1 items-center cursor-pointer">
                    Advanced options <Icon size={20} />{" "}
                    <div className="lg:inline-block hidden relative group ml-auto">
                        <FiInfo className="text-base cursor-pointer" />
                        <span className="absolute bottom-full right-0 mb-2 w-56 break-words bg-gray-700 text-white text-sm px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                            Control the number of pages crawled. Choose an option to define the crawl depth.
                        </span>
                    </div>
                </span>
            </AIChatbotFormLabel>
            {isOptionsOpen && (
                <div className="flex lg:gap-4 gap-3">
                    <AIChatbotButton type="button" isActive={quality === "single_page"} onClick={() => setQuality("single_page")}>
                        Low
                    </AIChatbotButton>
                    <AIChatbotButton type="button" isActive={quality === "medium"} onClick={() => setQuality("medium")}>
                        Medium
                    </AIChatbotButton>
                    <AIChatbotButton type="button" isActive={quality === "high"} onClick={() => setQuality("high")}>
                        High
                    </AIChatbotButton>
                </div>
            )}
            {isOptionsOpen && <p className="lg:hidden block text-sm mt-2 mx-1">Note: Control the number of pages crawled. Choose an option to define the crawl depth.</p>}
        </div>
    );

    const modal = (
        <Modal isOpen={isModalOpen} style={Styles} onRequestClose={() => setIsModalOpen(false)} shouldCloseOnOverlayClick={false}>
            <div className="w-full flex justify-end">
                <IoCloseOutline
                    size={22}
                    style={{
                        cursor: "pointer",
                    }}
                    className="text-gray-700 hover:text-white rounded-full hover:shadow-lg  hover:bg-gray-400"
                    onClick={() => setIsModalOpen(false)}
                />
            </div>

            <h5 className="lg:text-xl text-base lg:font-semibold font-medium text-center lg:mt-4 mt-2">Setting up your WhatsApp AI chatbot...</h5>

            <center className="lg:my-6 my-4">{renderModalContent}</center>

            {message?.message && <p className="lg:text-base text-sm text-gray-800 text-center">{message?.message ?? "Reading your website. Hang on..."}</p>}
        </Modal>
    );

    if (version === "v2") {
        return (
            <form
                className="lg:p-7 px-4 py-6 bg-white h-full shadow-lg border border-solid border-gray-200 rounded-lg w-full"
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    background: "linear-gradient(129.06deg, #D6E9FF 1.84%, #E3CEFF 98.21%)",
                }}
            >
                <h4 className="text-6xl font-semibold text-gray-900 mb-6 font-degular">Create your AI Chatbot in few seconds!</h4>
                <div className="md:flex justify-between gap-4">
                    <div className="md:flex w-full gap-2 " style={{ maxWidth: "820px" }}>
                        {whatsAppNumberField}
                        {websiteURLField}
                    </div>
                    <button
                        className="lg:px-8 px-4 bg-black text-white lg:text-xl text-base font-bold w-full md:mt-8 md:w-36"
                        style={{ borderRadius: "4px", height: "50px" }}
                        type="submit"
                    >
                        {submitButtonLabel ?? "Create AI Chatbot"}
                    </button>
                </div>

                {modal}
            </form>
        );
    }

    return (
        <form className="lg:p-7 px-4 py-6 bg-white h-full shadow-lg border border-solid border-gray-200 rounded-lg" onSubmit={handleSubmit(onSubmit)}>
            <h4 className="text-2xl font-semibold text-gray-900 mb-6">Create your AI Chatbot in few seconds!</h4>
            {whatsAppNumberField}
            {websiteURLField}
            {advancedOptions}
            <button className="w-full lg:px-8 px-4 lg:py-5 py-3 bg-black text-white lg:text-xl text-base font-bold" style={{ borderRadius: "4px" }} type="submit">
                {submitButtonLabel ?? "Create AI Chatbot"}
            </button>
            {modal}
        </form>
    );
};
export default CreateYourAIChatbotForm;

type AIChatbotFormLabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
const AIChatbotFormLabel: React.FC<AIChatbotFormLabelProps> = (props) => {
    const { children, ...labelProps } = props;
    return (
        <label {...labelProps} className="w-fit text-base font-medium text-gray-800">
            {children}
        </label>
    );
};

type AIChatbotButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & { isActive: boolean };
const AIChatbotButton: React.FC<AIChatbotButtonProps> = (props) => {
    const { children, isActive, ...buttonProps } = props;
    return (
        <button
            {...buttonProps}
            className={`text-base lg:px-6 px-5 lg:py-1.5 py-1 border border-solid border-gray-200 rounded-md  ${
                isActive ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-white text-black hover:bg-gray-100"
            }`}
        >
            {children}
        </button>
    );
};
